import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, checkProfileExists, ZealProfile, getTarotToday, TarotPull, getDailyPulse, DailyPulse, getEssenceStatus } from '../api/astrology';
import { getMoonPhase, getMoonSign } from '../data/sacredCalendar';
import ZodiacWheel from '../components/shared/ZodiacWheel';
import { getCardImageUrl } from '../data/tarotImages';
import { TAROT_DESCRIPTIONS } from '../data/tarotDescriptions';
import AppNav from '../components/shared/AppNav';
import SpaceBackground from '../components/shared/SpaceBackground';
import SpaceBackgroundSimple from '../components/shared/SpaceBackgroundSimple';
import TutorialCard from '../components/TutorialCard';
import { hasSeen, markSeen } from '../utils/tutorialManager';

import { _ATTRIBUTE_ORDER, _COSMIC_WEATHER, getGreeting, getDisplayName, getCpsTierName } from '../data/todayData';
import { SectionLabel, Shimmer, CpsRingSkeleton, PulseSkeleton, ExploreCard } from '../components/today/TodayShared';
import CpsRing from '../components/today/CpsRing';
import AttributeSpotlight from '../components/today/AttributeSpotlight';
import DailyReflection from '../components/today/DailyReflection';
import PulseCarousel from '../components/today/PulseCarousel';
import '../styles/today.css';
import '../styles/dashboard.css';

/* ─── Constants (see src/data/todayData.ts) ─── */
void _ATTRIBUTE_ORDER; // reserved for future use
void _COSMIC_WEATHER; // reserved for future use
void getGreeting; // used in TodayPage JSX
void getCpsTierName; // reserved for future use

/* ─── Main Page ─── */
const TodayPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ZealProfile | null>(null);
  const [tarotToday, setTarotToday] = useState<TarotPull | null>(null);
  const [tarotLoading, setTarotLoading] = useState(true);
  const [pulse, setPulse] = useState<DailyPulse | null>(null);
  const [pulseLoading, setPulseLoading] = useState(true);
  // Removed heroSlide carousel state - single tarot card now
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [essenceBalance, setEssenceBalance] = useState<number | null>(null);
  const [essenceTitle, setEssenceTitle] = useState<string>('');
  const [profileLoading, setProfileLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    const cachedAvatar = localStorage.getItem('zeal_avatar_url');
    if (cachedAvatar) setAvatarUrl(cachedAvatar);

    // First try: authenticated API calls
    let [profileData, tarot, pulseData, essenceData] = await Promise.all([
      getProfile(user.userId).catch(() => null),
      getTarotToday(user.userId).catch(() => null),
      getDailyPulse(user.userId).catch(() => null),
      getEssenceStatus(user.userId).catch(() => null),
    ]);

    // If auth profile failed, use public profile as immediate fallback (no auth needed)
    // Then schedule a re-fetch with auth for full data (timing boosts, tarot, etc.)
    let usedPublicFallback = false;
    if (!profileData) {
      console.warn('[TodayPage] Auth profile failed, trying public fallback...');
      const pubProfile = await checkProfileExists(user.userId);
      if (pubProfile && pubProfile.archetype) {
        profileData = pubProfile as any;
        usedPublicFallback = true;
      }
    }

    if (profileData) {
      setProfile(profileData);
      localStorage.setItem('zeal_onboarding_complete', 'true');
      if (!localStorage.getItem('zeal_birth_data')) localStorage.setItem('zeal_birth_data', 'true');
      localStorage.setItem('zeal_avatar_complete', 'true');
      localStorage.setItem('zeal_profile', JSON.stringify(profileData));
      if (profileData.name) {
        localStorage.setItem('zeal_display_name', profileData.name);
      }
      // Restore spiritual path from profile so Oracle/Calendar use correct tradition
      if ((profileData as any).spiritual_path) {
        localStorage.setItem('zeal_spiritual_path', (profileData as any).spiritual_path);
      }

      // If we used the public fallback or missed tarot/pulse, re-fetch with auth after delay
      if (usedPublicFallback || !tarot || !pulseData) {
        setTimeout(async () => {
          try {
            const [authProfile, authTarot, authPulse, authEssence] = await Promise.all([
              getProfile(user.userId).catch(() => null),
              !tarot ? getTarotToday(user.userId).catch(() => null) : Promise.resolve(tarot),
              !pulseData ? getDailyPulse(user.userId).catch(() => null) : Promise.resolve(pulseData),
              !essenceData ? getEssenceStatus(user.userId).catch(() => null) : Promise.resolve(essenceData),
            ]);
            if (authProfile) {
              setProfile(authProfile);
              localStorage.setItem('zeal_profile', JSON.stringify(authProfile));
              if (authProfile.name) localStorage.setItem('zeal_display_name', authProfile.name);
              // Restore oracle tradition from backend profile if not in localStorage
              if (authProfile.spiritual_path && !localStorage.getItem('zeal_oracle_tradition')) {
                const reversePathMap: Record<string, string> = {
                  spiritual: 'default', christian: 'christianity', hindu: 'hinduism',
                  buddhist: 'buddhism', jewish: 'judaism', muslim: 'islam', pagan: 'norse'
                };
                const tradition = reversePathMap[authProfile.spiritual_path] || 'default';
                localStorage.setItem('zeal_oracle_tradition', tradition);
                localStorage.setItem('zeal_spiritual_path', authProfile.spiritual_path);
              }
            }
            if (authTarot) setTarotToday(authTarot);
            setTarotLoading(false);
            if (authPulse) setPulse(authPulse);
            setPulseLoading(false);
            if (authEssence) {
              setEssenceBalance(authEssence.balance ?? null);
              if (authEssence.title?.name) setEssenceTitle(authEssence.title.name);
            }
          } catch (e) {
            console.warn('[TodayPage] Auth re-fetch failed:', e);
          }
        }, 2000);
      }
    } else {
      // Profile fetch failed - try one more time before assuming new user
      console.warn('[TodayPage] No profileData, attempting direct fetch...');
      const retryProfile = await getProfile(user.userId).catch(() => null);
      if (retryProfile && retryProfile.archetype) {
        // Profile exists! Set localStorage and let page load
        console.log('[TodayPage] Retry successful - profile found, setting flags');
        setProfile(retryProfile);
        localStorage.setItem('zeal_onboarding_complete', 'true');
        if (!localStorage.getItem('zeal_birth_data')) localStorage.setItem('zeal_birth_data', 'true');
        localStorage.setItem('zeal_avatar_complete', 'true');
        localStorage.setItem('zeal_profile', JSON.stringify(retryProfile));
        if (retryProfile.name) localStorage.setItem('zeal_display_name', retryProfile.name);
      } else {
        // Genuinely new user — send to onboarding
        if (!localStorage.getItem('zeal_birth_data')) {
          navigate('/birth-data', { replace: true });
          return;
        } else if (!localStorage.getItem('zeal_onboarding_complete')) {
          navigate('/onboarding', { replace: true });
          return;
        } else if (!localStorage.getItem('zeal_avatar_complete')) {
          navigate('/avatar-forge', { replace: true });
          return;
        }
      }
    }
    setProfileLoading(false);
    if (tarot) setTarotToday(tarot);
    setTarotLoading(false);
    if (pulseData) setPulse(pulseData);
    setPulseLoading(false);
    if (essenceData) {
      setEssenceBalance(essenceData.balance ?? null);
      if (essenceData.title?.name) setEssenceTitle(essenceData.title.name);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  // Show tutorial on first visit to Today page
  useEffect(() => {
    if (!profileLoading && profile && !hasSeen('todayPage')) {
      const timer = setTimeout(() => setShowTutorial(true), 500);
      return () => clearTimeout(timer);
    }
  }, [profileLoading, profile]);

  // Removed auto-rotate carousel

  if (!user) return null;

  if (profileLoading) {
    const loadingMessages = [
      'Aligning your stars...',
      'Consulting the cosmos...',
      'Reading the celestial map...',
      'Tuning into your frequency...',
      'Awakening your Oracle...',
    ];
    const loadMsg = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexDirection: 'column', background: '#0a0a0f',
        zIndex: 100,
      }}>
        <div style={{
          position: 'absolute', inset: 0, overflow: 'hidden', opacity: 0.4,
          background: `
            radial-gradient(ellipse at 20% 50%, rgba(201,168,76,0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 50%, rgba(100,149,237,0.06) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.04) 0%, transparent 40%)
          `,
        }} />
        <style>{`
          @keyframes zeal-orbit { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes zeal-breathe { 0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(201,168,76,0.15); } 50% { transform: scale(1.08); box-shadow: 0 0 60px rgba(201,168,76,0.3); } }
          @keyframes zeal-fadein { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes zeal-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        `}</style>
        <div style={{ position: 'relative', width: '140px', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: '-10px', border: '1px solid rgba(201,168,76,0.1)', borderTopColor: 'rgba(201,168,76,0.4)', borderRadius: '50%', animation: 'zeal-orbit 3s linear infinite' }} />
          <div style={{ position: 'absolute', inset: '-25px', border: '1px solid rgba(201,168,76,0.05)', borderBottomColor: 'rgba(201,168,76,0.2)', borderRadius: '50%', animation: 'zeal-orbit 5s linear infinite reverse' }} />
          <img src={`${process.env.PUBLIC_URL}/app-icon.jpg`} alt="Zeal Genesis" style={{ width: '90px', height: '90px', borderRadius: '50%', border: '2px solid rgba(201,168,76,0.3)', animation: 'zeal-breathe 2.5s ease-in-out infinite', position: 'relative', zIndex: 1 }} />
        </div>
        <p style={{ marginTop: '28px', fontFamily: "Cinzel, Georgia, serif", fontSize: '20px', color: '#c9a84c', letterSpacing: '3px', fontWeight: 500, animation: 'zeal-fadein 0.8s ease-out' }}>ZEAL GENESIS™</p>
        <p style={{ marginTop: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', animation: 'zeal-fadein 1.2s ease-out' }}>{loadMsg}</p>
        <div style={{ marginTop: '24px', width: '120px', height: '2px', background: 'rgba(201,168,76,0.1)', borderRadius: '1px', overflow: 'hidden' }}>
          <div style={{ width: '40%', height: '100%', background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)', animation: 'zeal-shimmer 1.5s ease-in-out infinite' }} />
        </div>
      </div>
    );
  }

  const moonPhase = getMoonPhase();
  const _moonSign = getMoonSign(new Date()); void _moonSign;
  const moonAttr = pulse?.moon?.attribute;

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', color: '#f4e4c1', overflowX: 'hidden' }}>
      {(() => {
        const p = (() => { try { return JSON.parse(localStorage.getItem('zeal_profile') || '{}'); } catch { return {}; } })();
        const bhUnlocked = !!p.black_hole_unlocked;
        const bhEnabled = bhUnlocked && localStorage.getItem('zeal_bh_enabled') === 'true';
        if (bhEnabled) return <SpaceBackground />;
        return <SpaceBackgroundSimple />;
      })()}
      <AppNav />
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: 'calc(env(safe-area-inset-top, 0px) + 75px) 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* ─── Greeting ─── */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: "'Inter', -apple-system, sans-serif", fontSize: '28px', fontWeight: 600, color: '#f4e4c1', margin: '0 0 8px', lineHeight: 1.2 }}>
            {(() => {
              const h = new Date().getHours();
              const name = user?.name?.split(' ')[0] || profile?.name?.split(' ')[0] || getDisplayName();
              if (h < 7) return `Good night, ${name}`;
              if (h < 12) return `Good morning, ${name}`;
              if (h < 17) return `Good afternoon, ${name}`;
              if (h < 21) return `Good evening, ${name}`;
              return `Good night, ${name}`;
            })()}
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', margin: 0, fontStyle: 'italic', lineHeight: 1.5 }}>
            {(() => {
              const h = new Date().getHours();
              const arch = profile?.archetype || '';
              const name = user?.name?.split(' ')[0] || profile?.name?.split(' ')[0] || getDisplayName();
              const nightLines = [
                'Put the screen down. Your sleep is sacred.',
                'The blue light can wait. Your rest cannot.',
                'Tonight, let stillness be your medicine.',
                'Close your eyes before the scroll finds you.',
                'Your body is asking for rest. Listen to it.',
                'The best thing you can do right now is nothing.',
                'Sleep is the oldest healer. Let it work.',
                'Dreams process what waking won\'t. Give them the material they need — silence.',
                'Your subconscious does its deepest work in the dark. Trust it.',
                'The moon doesn\'t apologize for disappearing. Neither should you.',
                'Rest is not weakness. It is the quietest form of power.',
                'Whatever went unresolved today — release it. The night will hold it for you.',
                'Your nervous system has been carrying you all day. Thank it with rest.',
                'The ancients knew: the hours before midnight heal twice as deep.',
                'What if tonight, instead of scrolling, you simply breathed until sleep came?',
                arch ? `Even the ${arch} must return to the void to be reborn. Sleep.` : 'Return to the void tonight. Be reborn tomorrow.',
              ];
              const morningLines = [
                `${name || 'You'}, you are capable of more than you know.`,
                'Today, lead with confidence. The universe noticed you.',
                'You deserve the space you take up in this world.',
                'Your presence alone shifts the energy of every room.',
                'Start today like you already won. Because you did.',
                `${arch ? `A ${arch} doesn\'t wait for permission.` : 'Don\'t wait for permission.'}`,
                'Carry yourself like everything is already yours.',
                'The first thought you think today becomes the filter for everything after.',
                'You are not behind. You are exactly where your soul needs you.',
                'Your ancestors survived everything so you could stand here this morning. Act like it.',
                'Before the world tells you who to be — decide for yourself.',
                'Sunlight on your skin is the oldest prayer. Step into it.',
                'Today is not a rehearsal. This is the performance.',
                `${arch ? `The world doesn\'t know what a ${arch} looks like when fully unleashed. Show them.` : 'The world doesn\'t know what you look like fully unleashed. Show them.'}`,
                'Your energy is your currency. Spend it on things that make you larger.',
                'What if today was the day everything changed? Would you be ready?',
              ];
              const dayLines = [
                arch ? `Even a ${arch} needs to pause. Have you breathed deeply today?` : 'Have you taken a real breath today?',
                'Your body carries what your mind forgets. Stretch.',
                'A glass of water right now would change your next hour.',
                'Look away from the screen. Give your eyes 20 seconds of distance.',
                'When did you last eat something that actually nourished you?',
                'Your posture right now is telling your brain how to feel.',
                'One small walk can shift your entire afternoon.',
                'The tension in your shoulders isn\'t yours to carry all day.',
                'Your body is a temple that forgets it\'s sacred. Remind it with movement.',
                'The vagus nerve connects your heart to your calm. Breathe into your belly.',
                'Cortisol or calm — your next choice decides which one wins.',
                'You\'ve been in your head all day. Drop into your body for 30 seconds.',
                'What would your future self thank you for doing right now?',
                'Your nervous system doesn\'t know the difference between a real threat and an email. Check in.',
                'The body keeps the score. What is yours keeping right now?',
                arch ? `A ${arch} running on empty is still running — but for how long? Refuel.` : 'Running on empty is still running — but for how long? Refuel.',
                'Hydration is the most boring miracle. Go get some water.',
                'The space between stimulus and response is where your freedom lives. Widen it.',
              ];
              const eveningLines = [
                'Start winding down. Your tomorrow begins now.',
                'The day is releasing you. Let it go.',
                'Dim the lights. Your body is already preparing for sleep.',
                'What you consume now — content or food — your body holds overnight.',
                'This is the hour between doing and being. Choose being.',
                'Light movement, not heavy food. Your sleep will thank you.',
                'The psyche integrates the day\'s events during the evening transition. Be gentle with it.',
                'What you do in the last hour of waking writes the first chapter of tomorrow.',
                'The stars are coming out. That\'s your signal too.',
                'Your cortisol should be falling. If it\'s not — what are you still fighting?',
                'Tonight, write down one thing you\'re releasing. Then actually release it.',
                arch ? `The ${arch} knows when to retreat. This is that time.` : 'The wise warrior knows when to retreat. This is that time.',
                'A warm drink, a slow breath, a soft light. That\'s all the ritual you need.',
                'The day had its say. Now let silence have yours.',
              ];
              let pool = dayLines;
              if (h < 7) pool = nightLines;
              else if (h < 10) pool = morningLines;
              else if (h >= 19) pool = eveningLines;
              else if (h >= 22) pool = nightLines;
              const dayIndex = Math.floor(Date.now() / (1000 * 60 * 30));
              return pool[dayIndex % pool.length];
            })()}
          </p>
        </div>

        <SectionLabel text="Today's Reflection" />
        <DailyReflection moonAttribute={moonAttr} archetype={profile?.archetype} />
        <section aria-label="Your practice hub" style={{ marginBottom: '24px', padding: '18px', background: '#171420', border: '1px solid #756236', borderRadius: '16px' }}>
          <h2 style={{ color: '#f4e4c1', fontSize: '19px' }}>Your Practice</h2>
          <p style={{ color: '#d8cfbc' }}>Choose a practice, reflect on your experience, and revisit it in Journey.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {[
              { path: '/essence', label: 'Attribute Trials & Soul Essence' },
              { path: '/tarot', label: 'Tarot & reflection' },
              { path: '/calendar', label: 'Moon & meditation' },
              { path: '/journey', label: 'Continue your journal' },
            ].map(item => <button key={item.path} onClick={() => navigate(item.path)} style={{ minHeight: '44px', padding: '10px', color: '#f4e4c1', background: '#282130', border: '1px solid #756236', borderRadius: '8px' }}>{item.label}</button>)}
          </div>
        </section>

        {/* ─── Zodiac Wheel ─── */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <ZodiacWheel size={Math.min(400, window.innerWidth - 40)} />
        </div>

        {/* ─── CPS Ring + Archetype ─── */}
        {profileLoading ? <CpsRingSkeleton /> : profile && profile.cps > 0 && (
          <CpsRing
            cps={profile.cps}
            archetype={profile.archetype || 'Seeker'}
            subArchetype={(profile as any).sub_archetype}
            attributes={profile.final_attributes || {}}
            moonAttribute={moonAttr}
            onClick={() => navigate('/profile')}
          />
        )}

        {/* ─── Cosmic Pulse ─── */}
        <SectionLabel text="Your Cosmic Pulse" />
        {pulseLoading ? <PulseSkeleton /> : pulse ? <PulseCarousel pulse={pulse} /> : (
          <div style={{ marginBottom: '24px', borderRadius: '16px', padding: '24px 20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.1)', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', margin: 0 }}>
              {moonPhase.icon} Your cosmic pulse is aligning...
            </p>
          </div>
        )}

        {/* ─── Today's Guidance ─── */}
        <SectionLabel text="Today's Guidance" />
        <div
          className="glow-tarot"
          onClick={() => navigate('/tarot')}
          style={{ position: 'relative', borderRadius: '18px', padding: '3px', cursor: 'pointer', transition: 'transform 0.3s ease', marginBottom: '24px' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
        >
          <div style={{ background: '#0e0e14', borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', justifyContent: 'center' }}>
            <div style={{
              flexShrink: 0, width: '100px', height: '190px', borderRadius: '8px', overflow: 'hidden', marginLeft: '4px',
              boxShadow: tarotToday
                ? tarotToday.is_reversed
                  ? '0 4px 24px rgba(155,89,182,0.3), 0 0 40px rgba(155,89,182,0.1)'
                  : '0 4px 24px rgba(201,168,76,0.3), 0 0 40px rgba(201,168,76,0.1)'
                : '0 4px 20px rgba(0,0,0,0.4)',
            }}>
              {tarotToday ? (
                <img src={getCardImageUrl(tarotToday.card) || '/tarot/major_00.jpg'} alt={tarotToday.card.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transform: tarotToday.is_reversed ? 'rotate(180deg)' : 'none' }} />
              ) : (
                <img src={`${process.env.PUBLIC_URL}/tarot-card-back.jpg`} alt="Draw your card"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85, filter: 'brightness(0.9)' }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center', paddingLeft: '4px', paddingRight: '4px' }}>
              {tarotToday ? (
                <>
                  <p style={{ fontSize: '11px', letterSpacing: '2px', color: 'rgba(201,168,76,0.8)', textTransform: 'uppercase', marginBottom: '6px', textAlign: 'center' }}>
                    Today's Card
                  </p>
                  {tarotToday.is_reversed && (
                    <p style={{ fontSize: '10px', color: '#9b59b6', background: 'rgba(155,89,182,0.12)', padding: '2px 7px', borderRadius: '4px', letterSpacing: '1px', display: 'inline-block', marginBottom: '6px' }}>↻ Reversed</p>
                  )}
                  <h3 style={{ fontFamily: "Cinzel, serif", fontSize: '1.2rem', color: tarotToday.is_reversed ? '#9b59b6' : '#f4e4c1', marginBottom: '8px' }}>{tarotToday.card.name}</h3>
                  {(() => {
                    const desc = TAROT_DESCRIPTIONS[(tarotToday.card as any).id];
                    if (!desc) return null;
                    const text = tarotToday.is_reversed ? desc.reversed : desc.upright;
                    const snippet = text ? text.substring(0, 100).replace(/\s+\S*$/, '') + '…' : '';
                    return (
                      <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', margin: '0 0 12px', lineHeight: '1.5' }}>
                        {snippet}
                      </p>
                    );
                  })()}
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'rgba(201,168,76,0.7)', fontWeight: 600, fontFamily: 'Cinzel, serif' }}>Check Tarot page for more details</span>
                  </div>

                </>
              ) : tarotLoading ? (
                <>
                  <Shimmer width="80px" height="10px" style={{ marginBottom: '10px' }} />
                  <Shimmer width="160px" height="20px" style={{ marginBottom: '12px' }} />
                  <Shimmer width="100%" height="14px" style={{ marginBottom: '6px' }} />
                  <Shimmer width="80%" height="14px" />
                </>
              ) : (
                <>
                  <p style={{ fontSize: '11px', letterSpacing: '2px', color: '#d4a574', textTransform: 'uppercase', marginBottom: '8px' }}>Daily Guidance</p>
                  <h3 style={{ fontFamily: "Cinzel, serif", fontSize: '1.3rem', color: '#f4e4c1', marginBottom: '10px' }}>Your Card Awaits</h3>
                  <div style={{
                    display: 'inline-block', padding: '8px 20px', borderRadius: '20px',
                    background: 'linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.05))',
                    border: '1px solid rgba(201,168,76,0.25)',
                    fontSize: '12px', color: '#c9a84c', fontWeight: 600, letterSpacing: '1px',
                  }}>
                    Reveal Today's Card ✦
                  </div>
                </>
              )}
            </div>
          </div>

        </div>



        {/* ─── The Oracle ─── */}
        <SectionLabel text="The Oracle" />
        <div
          onClick={() => navigate('/oracle')}
          className="glow-oracle"
          style={{
            borderRadius: '18px', padding: '24px',
            background: 'linear-gradient(145deg, rgba(155,89,182,0.06), rgba(201,168,76,0.03), rgba(255,255,255,0.02))',
            cursor: 'pointer', marginBottom: '24px',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
            <img src="/oracle-avatar.jpg" alt="Oracle" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(155,89,182,0.25)', boxShadow: '0 0 12px rgba(155,89,182,0.15)' }} />
            <div>
              <span style={{ fontSize: '17px', color: '#f4e4c1', fontWeight: 600, fontFamily: "Cinzel, serif", display: 'block' }}>Talk to the Oracle</span>
              <span style={{ fontSize: '11px', color: 'rgba(155,89,182,0.85)', fontWeight: 500, letterSpacing: '0.5px' }}>YOUR MYSTIC GUIDE</span>
            </div>
          </div>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, margin: '0 0 6px', fontStyle: 'italic' }}>
            {(() => {
              const h = new Date().getHours();
              const arch = profile?.archetype || '';
              const name = (profile as any)?.name?.split(' ')[0] || '';
              const greeting = h < 7 ? 'The quiet hours hold secrets.'
                : h < 12 ? 'The morning light reveals what the night concealed.'
                : h < 17 ? 'The midday sun illuminates your path.'
                : h < 21 ? 'The evening star invites you inward.'
                : 'The night sky whispers truths only the still can hear.';
              const hooks: string[] = [
                arch ? `What part of being a ${arch} feels heavy right now?` : 'What feels heavy right now?',
                'What are you avoiding that deserves your attention?',
                name ? `${name}, what would you do if fear wasn't a factor?` : 'What would you do if fear wasn\'t a factor?',
                'What pattern keeps showing up in your life lately?',
                'If your soul could speak one sentence right now, what would it say?',
                arch ? `Every ${arch} has a shadow. Have you met yours today?` : 'Have you met your shadow today?',
                'What conversation are you not having — but need to?',
                name ? `${name}, when did you last surprise yourself?` : 'When did you last surprise yourself?',
                'What would the bravest version of you do next?',
                'Something is shifting in you. Can you feel it?',
                'What did you dream about? There might be a message in it.',
                'Is the story you\'re telling yourself actually true?',
                arch ? `As a ${arch}, what gift are you not using right now?` : 'What gift of yours are you not using right now?',
                'Who in your life needs to hear something you haven\'t said?',
                'What small thing keeps nagging at your mind?',
              ];
              const dayIndex = Math.floor(Date.now() / (1000 * 60 * 30));
              const hook = hooks[dayIndex % hooks.length];
              return `${greeting} ${hook}`;
            })()}
          </p>
          <div style={{ marginTop: '12px' }}>
            <span style={{ fontSize: '14px', color: 'rgba(155,89,182,0.7)', fontWeight: 600, fontFamily: "Cinzel, serif" }}>Begin a conversation</span>
          </div>
        </div>

        {/* ─── Today's Reflection ─── */}

        {/* ─── Soul Essence ─── */}
        <SectionLabel text="Soul Essence · Earned through participation" />
        <div
          onClick={() => navigate('/essence')}
          style={{
            borderRadius: '18px',
            padding: '24px',
            background: 'linear-gradient(145deg, rgba(100,181,246,0.06), rgba(155,89,182,0.03), rgba(255,255,255,0.02))',
            border: '1px solid rgba(100,181,246,0.15)',
            cursor: 'pointer',
            marginBottom: '24px',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
            <img
              src="/soul-essence-crystal.jpg"
              alt="Soul Essence"
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid rgba(100,181,246,0.25)',
                boxShadow: '0 0 12px rgba(100,181,246,0.15)'
              }}
            />
            <div>
              <span style={{ fontSize: '17px', color: '#f4e4c1', fontWeight: 600, fontFamily: "Cinzel, serif", display: 'block' }}>Soul Essence</span>
              <span style={{ fontSize: '11px', color: 'rgba(100,181,246,0.85)', fontWeight: 500, letterSpacing: '0.5px' }}>YOUR SPIRITUAL CURRENCY</span>
            </div>
          </div>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, margin: '0 0 6px' }}>
            The currency of your spiritual journey. Earn through reflection, growth, and practice to unlock Sacred Appearances and express your evolving identity.
          </p>
          <div style={{ marginTop: '12px' }}>
            <span style={{ fontSize: '14px', color: 'rgba(100,181,246,0.7)', fontWeight: 600, fontFamily: "Cinzel, serif" }}>Collect your essence</span>
          </div>
        </div>

        {/* Sacred Spreads removed from Today page — accessible via Tarot page */}

        {/* ─── Explore ─── */}

        {/* Sacred Reports */}
        <h3 style={{
          fontFamily: "Cinzel, serif", fontSize: '22px', color: '#f4e4c1',
          marginBottom: '16px', letterSpacing: '1px',
        }}>Soul Blueprints</h3>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px', marginBottom: '32px',
        }}>
          {[
            { path: '/reports/relationship', img: 'product-relationship.jpg', title: 'Relationship Blueprint', desc: 'How you love, your shadows, and your growth path' },
            { path: '/reports/parenting', img: 'product-parenting.jpg', title: 'Parenting Compass', desc: 'Your parental archetype & legacy blueprint' },
            { path: '/reports/future', img: 'product-timing.jpg', title: 'The Timing Blueprint', desc: 'Strategic forecast & timing blueprint' },
          ].map((r, i) => (
            <button
              key={i}
              onClick={() => navigate(r.path)}
              style={{
                padding: '0', background: 'none',
                border: '1px solid rgba(212,165,116,0.2)', borderRadius: '14px',
                cursor: 'pointer', textAlign: 'left', overflow: 'hidden',
              }}
            >
              <div style={{
                height: '120px',
                backgroundImage: `linear-gradient(180deg, transparent 30%, rgba(10,10,26,0.85) 100%), url(${process.env.PUBLIC_URL}/${r.img})`,
                backgroundSize: 'cover', backgroundPosition: 'center',
              }} />
              <div style={{ padding: '12px 16px 16px' }}>
                <span style={{ fontSize: '16px', color: '#f4e4c1', fontWeight: 600, display: 'block' }}>{r.title}</span>
                <span style={{ fontSize: '13px', color: '#998877', display: 'block', marginTop: '4px', lineHeight: '1.4' }}>{r.desc}</span>
              </div>
            </button>
          ))}
        </div>

      </div>

      {/* Tutorial - First Visit */}
      {showTutorial && (
        <TutorialCard
          title="Follow the Stars"
          subtitle="The sky is personal"
          body="The Zodiac wheel at the top shows the exact positions of the planets, sun, and moon each day. Below it, your Cosmic Pulse offers personalized timing guidance: when to push forward, when to reflect, and when the stars align for breakthroughs."
          accent="#3498db"
          onClose={() => {
            setShowTutorial(false);
            markSeen('todayPage');
          }}
        />
      )}

      {/* Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes glowCps {
          0%, 100% { border-color: rgba(201,168,76,0.12); box-shadow: 0 0 0 rgba(201,168,76,0); }
          50% { border-color: rgba(201,168,76,0.35); box-shadow: 0 0 15px rgba(201,168,76,0.1); }
        }
        @keyframes glowPulse {
          0%, 100% { border-color: rgba(74,158,237,0.12); box-shadow: 0 0 0 rgba(74,158,237,0); }
          50% { border-color: rgba(74,158,237,0.3); box-shadow: 0 0 15px rgba(74,158,237,0.08); }
        }
        @keyframes glowTarot {
          0%, 100% { border-color: rgba(230,126,34,0.12); box-shadow: 0 0 0 rgba(230,126,34,0); }
          50% { border-color: rgba(230,126,34,0.35); box-shadow: 0 0 15px rgba(230,126,34,0.1); }
        }
        @keyframes glowOracle {
          0% { border-color: rgba(155,89,182,0.15); box-shadow: 0 0 0 rgba(155,89,182,0); }
          33% { border-color: rgba(201,168,76,0.2); box-shadow: 0 0 12px rgba(155,89,182,0.1); }
          66% { border-color: rgba(72,201,176,0.15); box-shadow: 0 0 8px rgba(201,168,76,0.06); }
          100% { border-color: rgba(155,89,182,0.15); box-shadow: 0 0 0 rgba(155,89,182,0); }
        }
        @keyframes glowReflection {
          0%, 100% { border-color: rgba(72,201,176,0.12); box-shadow: 0 0 0 rgba(72,201,176,0); }
          50% { border-color: rgba(72,201,176,0.3); box-shadow: 0 0 15px rgba(72,201,176,0.08); }
        }
        @keyframes glowSpreads {
          0%, 100% { border-color: rgba(231,76,139,0.12); box-shadow: 0 0 0 rgba(231,76,139,0); }
          50% { border-color: rgba(231,76,139,0.3); box-shadow: 0 0 15px rgba(231,76,139,0.08); }
        }
        .glow-cps { animation: glowCps 6s ease-in-out infinite; border: 1px solid rgba(201,168,76,0.12); }
        .glow-pulse { animation: glowPulse 6s ease-in-out infinite; border: 1px solid rgba(74,158,237,0.12); }
        .glow-tarot { animation: glowTarot 6s ease-in-out infinite; border: 1px solid rgba(230,126,34,0.12); }
        .glow-oracle { animation: glowOracle 6s ease-in-out infinite; border: 1px solid rgba(155,89,182,0.15); }
        .glow-reflection { animation: glowReflection 6s ease-in-out infinite; border: 1px solid rgba(72,201,176,0.12); }
        .glow-spreads { animation: glowSpreads 6s ease-in-out infinite; border: 1px solid rgba(231,76,139,0.12); }
      `}</style>
    </div>
  );
};

export default TodayPage;
